import { validateGuard } from 'components/shared/parsing/parsing.js'
import { createValidate } from 'typia'


export type IQuotationError = {
  title: string
  message: string
}


export class QuotationError extends Error {
  public readonly status: number
  public readonly title: string

  constructor(status: number, title: string, message: string) {
    super(message)

    this.name = this.constructor.name
    this.status = status
    this.title = title

    // This is needed to restore the prototype chain in TypeScript.
    // https://github.com/Microsoft/TypeScript/issues/13965#issuecomment-278570200
    Object.setPrototypeOf(this, new.target.prototype)
  }
}


export class BadRequestError extends QuotationError {
  constructor(title: string, message: string) {
    super(400, title, message)
  }
}


export class UnauthorizedError extends QuotationError {
  constructor(title: string, message: string) {
    super(401, title, message)
  }
}


export class NotFoundError extends QuotationError {
  constructor(title: string, message: string) {
    super(404, title, message)
  }
}


export class InternalServerError extends QuotationError {
  constructor(title: string, message: string) {
    super(500, title, message)
  }
}


export type StatusCodeToError<T extends number> =
  T extends 400 ? BadRequestError
    : T extends 401 ? UnauthorizedError
      : T extends 404 ? NotFoundError
        : T extends 500 ? InternalServerError
          : QuotationError; // Fallback for any other status code


const statusToErrorClassMap: Record<number, new (title: string, message: string) => QuotationError> = {
  400: BadRequestError,
  401: UnauthorizedError,
  404: NotFoundError,
  500: InternalServerError,
}


const isDefined = <T>(value: unknown): value is T => value !== undefined && value !== null

const validate = createValidate<IQuotationError>()


export function parseQuotationError<const T extends readonly number[]>(
  status: number,
  data: unknown,
  expectedErrorStatuses: T,
): StatusCodeToError<T[number]> {
  if (!expectedErrorStatuses.includes(status))
    throw new Error(`Unexpected Quotation error status received: ${status}. Expected one of [${expectedErrorStatuses}].`)

  if (!isDefined(data)) {
    return new QuotationError(status, 'No error data', 'No error message') as StatusCodeToError<T[number]>
  }

  validateGuard(validate, data, 'quotation error')

  const ErrorClass = statusToErrorClassMap[status]

  const error = ErrorClass
    ? new ErrorClass(data.title, data.message)
    : new QuotationError(status, data.title, data.message)

  return error as StatusCodeToError<T[number]>
}
